/**
 * useOutreachDrafts — the send wizard's drafts (ruling 46): the paged list plus
 * bulk approve and the edit-only patch. Every action invalidates the drafts
 * list, the rows and the counts: approving a draft both shrinks the queue and
 * changes the row's status pill/next-send column.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OutreachApproveDraftsInput, OutreachDraftPage, OutreachMessage } from '@discovr/types';
import { campaignKeys } from '../queryKeys';
import {
  approveOutreachDrafts,
  fetchOutreachDrafts,
  fetchTemplateStepText,
  patchOutreachDraft,
  writeFirstTouchByHand,
  type OutreachDraftEdit,
  type OutreachDraftsParams,
} from '../api/outreachApi';

export function useOutreachDrafts(campaignId: string, params: OutreachDraftsParams = {}, options: { enabled?: boolean; scope?: string } = {}) {
  return useQuery<OutreachDraftPage>({
    queryKey: campaignKeys.outreachDrafts(campaignId, params, options.scope),
    queryFn: () => fetchOutreachDrafts(campaignId, params),
    enabled: options.enabled ?? true,
  });
}

export interface PatchDraftVars {
  entryId: string;
  messageId: number;
  edit: OutreachDraftEdit;
}

export function useDraftActions(campaignId: string) {
  const qc = useQueryClient();

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: campaignKeys.outreachDraftsAll(campaignId) });
    void qc.invalidateQueries({ queryKey: campaignKeys.outreachRowsAll(campaignId) });
    void qc.invalidateQueries({ queryKey: campaignKeys.outreachCounts(campaignId) });
    void qc.invalidateQueries({ queryKey: campaignKeys.outreachBulkSends(campaignId) });
  };

  const approveMany = useMutation<{ operationId: string }, Error, OutreachApproveDraftsInput>({
    mutationFn: (input) => approveOutreachDrafts(campaignId, input, crypto.randomUUID()),
    onSuccess: invalidate,
  });

  const patch = useMutation<OutreachMessage, Error, PatchDraftVars>({
    mutationFn: (v) => patchOutreachDraft(v.entryId, v.messageId, v.edit, crypto.randomUUID()),
    onSuccess: invalidate,
  });

  const templateFirstTouch = useMutation<void, Error, TemplateFirstTouchVars>({
    mutationFn: async (v) => {
      const step = await fetchTemplateStepText(v.entryId, 1, v.templateId);
      await writeFirstTouchByHand(
        v.entryId,
        { subject: step.subject ?? '', body: step.body, bodyHtml: step.bodyHtml, templateId: v.templateId, fromTemplate: true },
        crypto.randomUUID(),
      );
    },
    // Awaited, so the wizard reads the parked draft back before it stops showing the item as writing.
    onSettled: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: campaignKeys.outreachDraftsAll(campaignId) }),
        qc.invalidateQueries({ queryKey: campaignKeys.outreachRowsAll(campaignId) }),
        qc.invalidateQueries({ queryKey: campaignKeys.outreachCounts(campaignId) }),
      ]);
    },
  });

  return { approveMany, patch, templateFirstTouch };
}

export interface TemplateFirstTouchVars {
  entryId: string;
  templateId?: number;
}
